import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import {
  AlertTriangle,
  Banknote,
  BarChart3,
  Calculator,
  HelpCircle,
  Wrench,
} from "lucide-react";

export const FinancialOverviewSection: React.FC<{ data: any }> = ({ data }) => {
  const currency = useCurrency();
  const assetSummaryCards = [
    {
      title: "Total Asset Value",
      count: `${currency} ${
        data?.result?.financialKpis?.totalAssetValue?.value ?? "NA"
      }`,
      trendSummary: "Current book value",
      icon: <Banknote className="text-green-500" />,
      tooltip:
        "The net book value of all assets in the portfolio after accounting for depreciation.",
    },
    {
      title: "Replacement Value",
      count: `${currency} ${
        data?.result?.financialKpis?.replacementValue?.value ?? "NA"
      }`,
      trendSummary: "Current replacement cost",
      icon: <Banknote className=" text-blue-500" />,
      tooltip:
        "The total estimated cost to replace every asset in the portfolio at today's prices.",
    },
    {
      title: "Budget Variance",
      count: `${data?.result?.financialKpis?.budgetVariance?.value ?? "NA"} ${
        data?.result?.financialKpis?.budgetVariance?.unit ?? "NA"
      }`,
      trendSummary: "vs planned spending",
      icon: <BarChart3 className=" text-purple-500" />,
      tooltip:
        "Measures financial performance against the budget. A positive value indicates underspending, while a negative value indicates overspending.",
    },
    {
      title: "Maintenance Spend",
      count: `${currency} ${
        data?.result?.financialKpis?.maintenanceSpend?.value ?? "NA"
      } ${data?.result?.financialKpis?.maintenanceSpend?.unit ?? "NA"}`,
      trendSummary: "This month",
      icon: <Wrench className="text-orange-500" />,
      tooltip:
        "The total cost incurred for all maintenance activities (planned and unplanned) during the selected period.",
    },
    {
      title: "Emergency Repairs",
      count: `${currency} ${
        data?.result?.financialKpis?.emergencyRepairs?.value ?? "NA"
      } ${data?.result?.financialKpis?.emergencyRepairs?.unit ?? "NA"}`,
      trendSummary: "Unplanned costs",
      icon: <AlertTriangle className="text-red-500" />,
      tooltip:
        "The total cost of unplanned, reactive repairs. This is a key indicator of asset reliability and operational risk.",
    },
  ];

  return (
    <>
      {/* Financial KPIs Overview - Top 5 Metrics in Same Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {assetSummaryCards.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.count ?? "NA"}
            trendSummary={card.trendSummary}
            icon={card.icon}
            tooltip={card.tooltip}
          />
        ))}
      </div>

      {/* Annual Depreciation */}
      <Card>
        <CardHeader>
          <div className="flex item-center gap-2">
            <div>
              <CardTitle className="flex items-center page-title gap-2">
                <Calculator className="w-5 h-5" />
                Annual Depreciation
              </CardTitle>
            </div>
            <div className="mt-1">
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    The total accounting depreciation expense for all assets for
                    the current fiscal year." On hover over a category: "Total
                    annual depreciation for all assets in the [Asset Class].
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold mb-4">
            {currency} {data?.result?.annualDepreciation?.totalValue ?? "NA"}{" "}
            {data?.result?.annualDepreciation?.unit ?? "NA"}
          </div>
          <div className="space-y-2">
            {data?.result?.annualDepreciation?.breakdown
              .slice(0, 3)
              .map((category, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm">{category.category ?? "NA"}</span>
                  <span className="text-sm font-medium">
                    {currency} {category.value}M
                  </span>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
};
